/**
 * Spec 041: the user-facing copy for live paper sessions, as data. Pure
 * constants and tiny lookups; nothing here reads the clock or the network.
 */

export const ACTIVITY_COPY: Record<string, string> = {
  starting: 'Starting up.',
  warming_up: 'Warming up on stored bars. It has not started trading.',
  waiting_for_feed: 'Waiting for the feed. No new entries until it returns.',
  running: 'Running.',
  stopping: 'Closing the position and stopping.',
  ended: 'Ended.',
}

/** Shown when `activity` is null or absent. */
export const ACTIVITY_NONE = '—'

export const WAITING_CLOSED = 'The market is closed; it resumes by itself.'
export const WAITING_DOWN = 'The market feed is down.'
export const STALLED_SENTENCE =
  'Stalled: no heartbeat for 30 s or more, but its container is still running. Nothing stops it automatically; you can stop it.'

export const STATE_PROBLEM_COPY = {
  not_written: 'Starting: waiting for the first state.',
  not_written_old: 'No state has been written yet. Check the Log tab.',
  missing: "The session's state file is missing.",
  unreadable: 'The state file could not be read.',
  too_large: 'The state file is too large to read.',
  malformed: 'The state file is malformed.',
  unsupported_version: 'The state file is from a newer version than this app understands.',
  none: 'No state.',
} as const

export function stateProblemOther(problem: string): string {
  return `No usable state (${problem}).`
}

/** Keyed by `status/end_reason`. */
export const ENDED_HEADLINES: Record<string, string> = {
  'stopped/user_stop': 'Stopped by you.',
  'stopped/user_stop_timed_out': 'Stopped, but the close was not confirmed.',
  'stopped/user_stop_killed': 'Stopped, but the close was not confirmed.',
  'interrupted/shutdown':
    'Interrupted: Docker or the machine shut down. The session did not restart by itself; start a new one if you want one.',
  'interrupted/shutdown_stop_timed_out': 'Interrupted: the container was killed or disappeared.',
  'interrupted/container_killed': 'Interrupted: the container was killed or disappeared.',
  'interrupted/container_missing': 'Interrupted: the container was killed or disappeared.',
  'failed/error': 'Failed: the strategy stopped with an error.',
  'failed/refused_validation': 'Refused at start: it never started trading.',
  'failed/refused_data_not_covered': 'Refused at start: it never started trading.',
  'failed/refused_feed_check': 'Refused at start: it never started trading.',
}

export function endedFallbackHeadline(status: string): string {
  return `Ended (${status}).`
}

export const POSITION_SENTENCES: Record<string, string> = {
  closed: 'The position was closed on paper.',
  flat: 'There was no open position.',
  possibly_open:
    'The close was not confirmed. The paper position may still be open. The last known position is shown below.',
  unknown: 'The position at the end is not known. The last known position is shown below.',
}

export const CLOSE_BASIS: Record<string, string> = {
  trade: 'closed at the last trade',
  bar: "closed at the bar's price",
  last_known_price: 'closed at the last known price',
}

export const STOP_EXPLANATION = 'Stopping closes the position on paper at the current price and ends the session.'

export const STOP_REASONS = {
  starting: 'The session is still starting. Stop it once it is running.',
  notReady:
    'Wait until the strategy has finished warming up. Stopping earlier can leave the paper position unconfirmed.',
  unknownStatus: "This session's status is not recognised, so it cannot be stopped from here.",
} as const

export const STOPPING_LABEL = 'Stopping…'

export const FIGURES_CAPTION = 'Paper account. P&L and equity move when a 1-minute bar closes, not with each trade.'
export const NOT_FINAL_CAPTION = 'Last known figures, not final.'
